import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Link,
  Breadcrumbs,
} from "@mui/material";
import {
  ArrowBack,
  Home,
  NavigateNext,
} from "@mui/icons-material";
import { productService } from "../services/products";
import { Product } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useTranslation } from "react-i18next";
import { getCategoryBreadcrumbLabel } from "../utils/breadcrumbs";
import ProductImageGallery from "../components/product-detail/ProductImageGallery";
import ProductMainInfo from "../components/product-detail/ProductMainInfo";
import ProductPurchaseCard from "../components/product-detail/ProductPurchaseCard";
import ProductAdditionalInfo from "../components/product-detail/ProductAdditionalInfo";

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { addToCart, items } = useCart();
  const { t } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [justAdded, setJustAdded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [imageZoom, setImageZoom] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const { data, error } = await productService.getProductById(id);
      if (error) {
        setError(t("products.failedToLoad"));
      } else if (data) {
        setProduct(data);
      }
    } catch (err) {
      setError(t("products.unexpectedError"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (product) {
      for (let i = 0; i < quantity; i++) {
        addToCart(product);
      }
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 2000);
    }
  };

  const isInCart = () => {
    return items.some((item) => item.product.id === product?.id);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case "excellent":
        return "#4caf50";
      case "good":
        return "#2196f3";
      case "fair":
        return "#ff9800";
      default:
        return "#757575";
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || t("products.productNotFound")}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/products")}
          variant="outlined"
        >
          {t("products.backToProducts")}
        </Button>
      </Container>
    );
  }

  const discountPercentage =
    product.zetta_price && product.price !== product.zetta_price
      ? Math.round(
          ((product.price - product.zetta_price) / product.price) * 100
        )
      : 0;

  // Mock data for specialties and categories based on the image
  const medicalSpecialties = [
    t("products.specialties.intensiveCriticalCare"),
    t("products.specialties.cardiology"),
    t("products.specialties.emergencyMedicine"),
    t("products.specialties.anesthesiaPerioperative"),
    t("products.specialties.laboratoryMedicalBiology"),
    t("products.specialties.gynecologyObstetrics"),
    t("products.specialties.pediatricsNeonatology"),
    t("products.specialties.radiology"),
  ];

  const productCategories = [
    t("products.productCategoriesList.medicalDiagnosticEquipment"),
    t("products.productCategoriesList.medicalMonitoringEquipment"),
    t("products.productCategoriesList.vitalSignsMonitor"),
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ minHeight: "100vh" }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" sx={{ color: "var(--text-disabled)" }} />}
          sx={{ mb: 2, fontSize: "0.875rem" }}
        >
          <Link
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: "0.875rem",
              "&:hover": { color: "var(--primary-color)" },
            }}
            onClick={() => navigate("/")}
          >
            <Home sx={{ mr: 0.5, fontSize: 16 }} />
            {t("nav.home")}
          </Link>
          {product.category && (
            <Link
              underline="hover"
              sx={{
                color: "var(--text-secondary)",
                cursor: "pointer",
                fontSize: "0.875rem",
                "&:hover": { color: "var(--primary-color)" },
              }}
              onClick={() =>
                navigate(`/category/${product.category as string}`)
              }
            >
              {getCategoryBreadcrumbLabel(t, product.category || undefined)}
            </Link>
          )}
          <Typography color="text.primary" sx={{ fontSize: "0.875rem" }}>
            {product.title}
          </Typography>
        </Breadcrumbs>

        <Paper
          sx={{
            p: 0,
            border: "none",
            boxShadow: 0,
            "&:hover": {
              border: "none",
              boxShadow: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
            }}
          >
            <ProductImageGallery
              images={product.images}
              title={product.title}
              selectedImage={selectedImage}
              onSelectImage={setSelectedImage}
              onZoom={() => setImageZoom(true)}
            />

            <ProductMainInfo
              title={product.title}
              categories={productCategories}
              specialties={medicalSpecialties}
              description={product.description}
            />

            <ProductPurchaseCard
              price={product.zetta_price || product.price}
              condition={product.condition || "good"}
              quantity={quantity}
              setQuantity={setQuantity}
              justAdded={justAdded}
              onAddToCart={handleAddToCart}
              isFavorite={isFavorite}
              setIsFavorite={setIsFavorite}
              getConditionColor={getConditionColor}
            />
          </Box>

          <ProductAdditionalInfo product={product} />
          {/* <SectionHeader /> */}
        </Paper>
        {/* </Container> */}
      </Box>
    </Container>
  );
};

export default ProductDetail;
